import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import AuthShowcase from "@/components/auth/AuthShowcase";

const REMEMBER_KEY = "agentos_remember_email";


export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) setEmail(saved);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      if (remember) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      nav("/app");
    } catch (err) {
      // 402 → tenant pending_payment / past_due / cancelled. Push the
      // user to the regularization page with a fresh checkout_token so
      // they can re-enter the Stripe flow without re-typing the password.
      const detail = err?.response?.data?.detail;
      const isBillingGate =
        err?.response?.status === 402
        || (detail && typeof detail === "object" && detail.reason === "subscription_required");
      if (isBillingGate && detail?.checkout_token) {
        sessionStorage.setItem("agentus_checkout_intent", JSON.stringify({
          registration_token: detail.checkout_token,
          tenant_id: null,
          email: detail.email,
          tenant_name: detail.tenant_name,
          tenant_status: detail.tenant_status,
        }));
        nav("/regularizar");
        return;
      }
      const msg = typeof detail === "string" ? detail : "Falha ao entrar";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen text-white flex overflow-hidden"
      style={{ backgroundColor: "#04060B" }}
    >
      {/* ───── LEFT — Cinematic live-WhatsApp showcase (desktop only) ───── */}
      <AuthShowcase />

      {/* ───── RIGHT — Auth surface (dark, continuous with the stage) ───── */}
      <div className="relative flex-1 lg:basis-1/2 lg:grow-0 lg:shrink-0 min-h-screen flex flex-col overflow-hidden">
        {/* Ambient glow behind the form */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(620px 480px at 50% 42%, rgba(96,140,255,0.07), transparent 65%), radial-gradient(700px 420px at 50% 115%, rgba(255,255,255,0.03), transparent 70%)",
          }}
        />
        {/* Subtle dot grid */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
            backgroundSize: "34px 34px",
          }}
        />

        <header className="relative z-10 px-5 sm:px-7 pt-5 sm:pt-6 pb-1 flex items-center justify-between">
          <img
            src="/logo-white.png"
            alt="AgentUS"
            className="h-[15px] sm:h-[16px] w-auto select-none opacity-95 lg:hidden"
            draggable="false"
            data-testid="login-logo"
          />
          <span className="hidden lg:block" />
          <a
            href="https://www.agentus.com.br"
            className="text-[10.5px] tracking-tight text-white/30 hover:text-white/80 transition-colors duration-200"
            data-testid="login-back-to-site"
          >
            Site institucional
          </a>
        </header>

        <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-2 sm:py-4">
          <div className="w-full max-w-[392px]">
            {/* Heading block */}
            <div className="text-center mb-6 sm:mb-7">
              <div
                className="text-[9.5px] uppercase tracking-[0.3em] text-white/30 font-medium mb-3 select-none"
                data-testid="login-tagline"
              >
                Automação Operacional Inteligente
              </div>
              <h1 className="font-heading text-[23px] sm:text-[27px] font-semibold tracking-[-0.025em] text-white leading-[1.15]">
                Bem-vindo de volta
              </h1>
              <p className="text-[12.5px] text-white/40 mt-1.5 tracking-tight">
                Acesse sua conta
              </p>
            </div>

            {/* Glass form */}
            <form
              onSubmit={submit}
              data-testid="login-form"
              className="rounded-[16px] px-5 sm:px-6 pt-5 pb-5 border border-white/[0.08]"
              style={{
                background: "rgba(255,255,255,0.035)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px -20px rgba(0,0,0,0.6)",
              }}
            >
              <div className="space-y-3.5">
                <Field label="Email">
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    data-testid="login-email-input"
                    className="login-input"
                  />
                </Field>

                <Field label="Senha">
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="login-password-input"
                      className="login-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid="login-toggle-password"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/35 hover:text-white transition-colors duration-150"
                      tabIndex={-1}
                      aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPwd ? <EyeSlash size={15} weight="regular" /> : <Eye size={15} weight="regular" />}
                    </button>
                  </div>
                </Field>
              </div>

              <div className="flex items-center justify-between mt-4 text-[12px]">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="login-remember-checkbox"
                    className="w-[14px] h-[14px] rounded-[3px] cursor-pointer"
                    style={{ accentColor: "#E2E8F0" }}
                  />
                  <span className="text-white/45 group-hover:text-white/75 transition-colors">Lembrar email</span>
                </label>
                <button
                  type="button"
                  onClick={() => nav("/forgot-password")}
                  className="text-white/45 hover:text-white transition-colors"
                  data-testid="login-forgot"
                >
                  Esqueci minha senha
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="login-submit"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full border-[1.5px] border-slate-900/25 border-t-slate-900 animate-spin" />
                    Entrando
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 group/btn">
                    Entrar
                    <ArrowRight size={13} weight="bold" className="opacity-60 transition-all duration-300 ease-out group-hover/btn:opacity-100 group-hover/btn:translate-x-0.5" />
                  </span>
                )}
              </button>
            </form>

            <p className="text-[12px] text-white/40 text-center mt-5 tracking-tight">
              Novo na AgentUS?{" "}
              <Link
                to="/register"
                data-testid="login-to-register"
                className="text-white font-semibold hover:underline underline-offset-[5px] decoration-[1.5px] transition-all"
              >
                Criar conta
              </Link>
            </p>
          </div>
        </main>

        <footer className="relative z-10 px-6 py-4 text-center">
          <p className="text-[10px] text-white/20 tracking-[0.04em]">
            © {new Date().getFullYear()} AgentUS · multi-tenant · audit-first · API-ready
            <span className="mx-2 text-white/10" aria-hidden>·</span>
            <Link
              to="/admin-master/login"
              data-testid="login-admin-access"
              className="text-white/20 hover:text-white/70 transition-colors duration-200"
            >
              Administração
            </Link>
          </p>
        </footer>
      </div>{/* /RIGHT auth surface */}

      <Toaster position="top-right" richColors />

      <style>{`
        .login-input {
          width: 100%;
          height: 42px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px;
          padding: 0 13px;
          font-size: 13.5px;
          color: #F1F5F9;
          outline: none;
          transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
        }
        .login-input::placeholder { color: rgba(255,255,255,0.28); }
        .login-input:hover { border-color: rgba(255,255,255,0.20); }
        .login-input:focus {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.55);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.06);
        }
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #10141C inset;
          -webkit-text-fill-color: #F1F5F9;
          caret-color: #F1F5F9;
          border-radius: 10px;
        }
        @media (min-width: 640px) {
          .login-input { height: 44px; font-size: 14px; }
        }

        .login-submit {
          margin-top: 18px;
          width: 100%;
          height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #0B0F17;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.005em;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.6),
            0 4px 18px -4px rgba(255,255,255,0.18);
          transition:
            background-color .25s ease,
            box-shadow .25s ease,
            transform .15s ease;
        }
        .login-submit:hover {
          background: #FFFFFF;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.7),
            0 6px 26px -4px rgba(255,255,255,0.30);
        }
        .login-submit:active {
          background: #E2E8F0;
          transform: translateY(0.5px);
        }
        .login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .login-submit:disabled:hover {
          background: #F8FAFC;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.6),
            0 4px 18px -4px rgba(255,255,255,0.18);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
